import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, CircularProgress, Chip, Avatar, Tooltip } from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import { PersonPinCircle, LocationOn, AccessTime } from '@mui/icons-material';
import L from 'leaflet';
import api from '../../api/axios';
import LoadingState from '../shared/LoadingState';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default icon issue
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { parseUtc } from '../../utils/time';


let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Custom Icons
const workerIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const workerOfflineIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-grey.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const jobIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const MapDashboard = () => {
    const [data, setData] = useState({ workers: [], jobs: [] });
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchData = async () => {
        try {
            const response = await api.get('/admin/map/data');
            setData(response.data);
            setLastUpdated(new Date());
        } catch (err) {
            console.error("Failed to load map data", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 30000); // Refresh every 30s
        return () => clearInterval(interval);
    }, []);

    if (loading && !data.workers.length) {
        return <LoadingState message="Loading live map..." />;
    }

    // Default center (Brisbane based on example addresses)
    const center = [-27.4698, 153.0251]; 

    return (
        <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, bgcolor: 'white', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h6" fontWeight={700}>Live Operations Map</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {data.workers.length} Team Members • {data.jobs.length} Active Jobs
                    </Typography>
                </Box>
                {lastUpdated && (
                    <Chip 
                        icon={<AccessTime />} 
                        label={`Updated ${lastUpdated.toLocaleTimeString()}`} 
                        variant="outlined" 
                        size="small" 
                    />
                )}
            </Box>
            
            <Box sx={{ flexGrow: 1, position: 'relative' }}>
                <MapContainer center={center} zoom={10} style={{ height: '100%', width: '100%' }}>
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    
                    {/* Workers */}
                    {data.workers.map(worker => {
                        if (!worker.lat || !worker.lng) return null;
                        return (
                            <Marker 
                                key={`w-${worker.id}`} 
                                position={[worker.lat, worker.lng]}
                                icon={worker.status === 'Active' ? workerIcon : workerOfflineIcon}
                            >
                                <Popup>
                                    <Box sx={{ textAlign: 'center', p: 1 }}>
                                        <Avatar sx={{ width: 32, height: 32, margin: '0 auto', mb: 1, bgcolor: 'primary.main' }}>
                                            {worker.name[0]}
                                        </Avatar>
                                        <Typography variant="subtitle2" fontWeight="bold">{worker.name}</Typography>
                                        <Typography variant="caption" display="block" color={worker.status === 'Active' ? 'success.main' : 'text.secondary'}>
                                            ● {worker.status}
                                        </Typography>
                                        {worker.last_seen && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Seen: {parseUtc(worker.last_seen).toLocaleTimeString()}
                                            </Typography>
                                        )}
                                    </Box>
                                </Popup>
                            </Marker>
                        );
                    })}

                    {/* Jobs */}
                    {data.jobs.map(job => {
                        if (!job.lat || !job.lng) return null;
                        return (
                            <Marker 
                                key={`j-${job.id}`} 
                                position={[job.lat, job.lng]}
                                icon={jobIcon}
                            >
                                <Popup>
                                    <Box sx={{ p: 0.5 }}>
                                        <Typography variant="subtitle2" fontWeight="bold">#{job.id} {job.description}</Typography>
                                        <Chip label={job.status} size="small" sx={{ my: 0.5, height: 20, fontSize: '0.7rem' }} />
                                        {job.client_name && (
                                            <Typography variant="caption" display="block">{job.client_name}</Typography>
                                        )}
                                        {job.scheduled_time && (
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                {new Date(job.scheduled_time).toLocaleString()}
                                            </Typography>
                                        )}
                                    </Box>
                                </Popup>
                            </Marker>
                        );
                    })}
                </MapContainer>
            </Box>
        </Box>
    );
};

export default MapDashboard;
